import { C, TIER } from "../../shared/theme";
import { todayKey, parseKey } from "../../shared/dates";
import { AddItemButton } from "../../shared/AddItemButton";
import { DoneTaskList } from "../tasks/DoneTaskList";
import { EditButton } from "../../shared/EditButton";
import { taskWords } from "../../shared/taskWords";
import { countdown, longDate, remindStart, shortDate, repeatWords } from "./keyDates";
import { MarkedLine, GoalDot } from "../../shared/MarkedLine";


// A small calendar page: the month on a band of the Key Dates colour, the day underneath.
export function DateLeaf({ date, size = 52 }) {
  const d = parseKey(date);
  return (
    <div aria-hidden="true" style={{ width: size, flexShrink: 0, borderRadius: 10, overflow: "hidden", border: `1px solid ${C.borderCard}`, background: C.surface, textAlign: "center" }}>
      <div style={{ background: TIER.keyDates, color: C.onTier, fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", padding: "3px 0", textTransform: "uppercase" }}>
        {d.toLocaleDateString("en-US", { month: "short" })}
      </div>
      <div style={{ fontSize: size > 48 ? 22 : 18, fontWeight: 800, color: C.text, padding: "3px 0 4px", fontVariantNumeric: "tabular-nums" }}>{d.getDate()}</div>
    </div>
  );
}

// "In 25 days": the one bit of colour on a card, once reminders have started (the date is on Radar).
// Before that it is grey.
const CountdownPill = ({ date, started }) => (
  <span style={{ display: "inline-block", marginTop: 6, fontSize: 13, fontWeight: 700, padding: "3px 9px", borderRadius: 100, background: started ? C.amberBg : C.surface2, color: started ? C.amber : C.textMid }}>
    {countdown(date)}
  </span>
);

// The goal a date is for: its colour dot and name. Nothing when the date has no goal.
const GoalTag = ({ goal }) => goal && <MarkedLine marker={<GoalDot goal={goal} />} label="Goal" style={{ marginTop: 3 }}>{goal.title}</MarkedLine>;

// "Saturday, October 24", or "Saturday, October 24 · Every week" for a date that repeats.
const dateWords = (keyDate) => longDate(keyDate.date) + repeatWords(keyDate);

// "1 of 3 done", once something is done.
const doneCount = (tasks) => {
  const done = tasks.filter(t => t.done).length;
  return done ? `${done} of ${tasks.length} done` : null;
};


// A Key Date still to come: the date, its countdown (grey until reminders start) and its tasks. The card doesn't say when
// reminders start (Craig, 25 Oct 2026: "remove the 'Reminders start (date)' text from the Key Date card"); the editor does.
// Cards start folded and say how many tasks are left; tap the date to open or fold it. Edit opens the date.
// Its tasks to do are every task list's rows (Craig, 29 Oct 2026: "on the Key Date card view, we need to show tasks the
// standard way"): row(task) draws one. The done ones follow, struck through, and untick with a tap of their circle, so a
// date that comes back again can be checked off again; tap one's words to open it. onUndo(task) saves and returns a promise.
export function KeyDateCard({ keyDate, goal = null, tasks, collapsed, onToggle, onEdit, row, onOpenTask, onUndo, onNewTask }) {
  const started = remindStart(keyDate.date, keyDate.remindBefore) <= todayKey();
  const progress = collapsed ? taskWords(tasks) : doneCount(tasks);
  const toDo = tasks.filter(t => !t.done), done = tasks.filter(t => t.done);
  // The count sits on the countdown's row, which has room, so the date keeps its own line.
  const listId = `key-date-tasks-${keyDate.id}`;
  return (
    <article style={{ background: C.surface, border: `1px solid ${C.borderCard}`, borderRadius: C.r, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "flex-start" }}>
        <button onClick={onToggle} aria-expanded={!collapsed} aria-controls={collapsed ? undefined : listId}
          style={{ flex: 1, minWidth: 0, display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 4px 12px 12px", background: "none", border: "none", textAlign: "left", cursor: "pointer", fontFamily: "inherit", color: C.text }}>
          <DateLeaf date={keyDate.date} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 17, fontWeight: 700, lineHeight: 1.3 }}>{keyDate.title}</span>
            <span style={{ display: "block", fontSize: 14, color: C.textMid, marginTop: 3, lineHeight: 1.35 }}>{dateWords(keyDate)}</span>
            <GoalTag goal={goal} />
            <span style={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 10 }}>
              <CountdownPill date={keyDate.date} started={started} />
              {progress && <span style={{ marginTop: 6, fontSize: 14, color: C.textMid }}>{progress}</span>}
              <span aria-hidden="true" style={{ marginTop: 6, fontSize: 12, color: C.textMid }}>{collapsed ? "▼" : "▲"}</span>
            </span>
          </span>
        </button>
        <EditButton label={`Edit ${keyDate.title}`} onClick={onEdit} style={{ margin: "6px 4px 0 0" }} />
      </div>
      {!collapsed && (
        <div id={listId} style={{ padding: "0 12px 4px" }}>
          {toDo.map(row)}
          {done.length > 0 && <DoneTaskList tasks={done} onUndo={onUndo} onOpen={onOpenTask} />}
          <AddItemButton onClick={onNewTask} />
        </div>
      )}
    </article>
  );
}

// A Key Date on one line: later dates, past dates, and the calendar's list. It says when reminders start,
// or once they have, how far away it is (or how long ago), and how many tasks it has left.
export function KeyDateLine({ keyDate, goal = null, tasks, past, onEdit, first }) {
  const start = remindStart(keyDate.date, keyDate.remindBefore);
  const when = past || start <= todayKey() ? countdown(keyDate.date) : `Reminders start ${shortDate(start)}`;
  return (
    <button onClick={onEdit}
      style={{ width: "100%", display: "flex", gap: 12, alignItems: "center", padding: "10px 12px", background: "none", border: "none", borderTop: first ? "none" : `1px solid ${C.border}`, textAlign: "left", cursor: "pointer", fontFamily: "inherit", color: C.text }}>
      <DateLeaf date={keyDate.date} size={44} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 16, fontWeight: 600, lineHeight: 1.3 }}>{keyDate.title}</span>
        <span style={{ display: "block", fontSize: 14, color: C.textMid, marginTop: 2, lineHeight: 1.35 }}>{when} · {taskWords(tasks, past)}{repeatWords(keyDate)}</span>
        <GoalTag goal={goal} />
      </span>
    </button>
  );
}
